import React, { useEffect, useState, FunctionComponent } from 'react';
import classnames from 'classnames';

import { keyPressHelper } from '@helpers/accessibility';
import { Attachment, isVideoType, PreviewType } from '@api/v4/attachments/attachmentTypes';
import { Asset, AssetTypes } from '@api/v4/assets/assetTypes';
import { get as getAttachment } from '@api/v4/attachments';
import { BFLoader } from '@components/common/loader/main';
import { TextButton } from '@components/library/button/index';
import { AudioPreview } from '@components/asset/modal/tabs/asset_details/asset_preview/AudioPreview';
import { BrightcoveVideoPreview } from '@components/asset/modal/tabs/asset_details/asset_preview/BrightcoveVideoPreview';
import { MuxVideoPreview } from '@components/asset/modal/tabs/asset_details/asset_preview/MuxVideoPreview';
import { ProcessingVideoPreview } from '@components/asset/modal/tabs/asset_details/asset_preview/ProcessingVideoPreview';
import { PDFPreview } from '@components/asset/modal/tabs/asset_details/asset_preview/PDFPreview';
import { ImagePreview } from '@components/asset/modal/tabs/asset_details/asset_preview/ImagePreview';
import { ExternalMediaPreview } from '@components/asset/modal/tabs/asset_details/asset_preview/ExternalMediaPreview';
import { TextPreview } from '@components/asset/modal/tabs/asset_details/asset_preview/TextPreview';
import { WEB_FRIENDLY_ATTACHMENT_TYPES } from '@helpers/attachment_conversion';
import FontClass from '@components/asset/shared/fontClass';

interface AssetPreviewProps {
  asset: Asset;
  assetType: AssetTypes;
  attachmentKeys: string[];
  assetBackgroundColor?: { backgroundColor: string };
  isDownloadable?: boolean;
  threeSixtyViewer?: boolean;
}

type UpdateSelectedAttachmentEvent = Event & { detail: { attachmentKey: string } };
type AssetPreviewSignature = ((asset: Asset) => JSX.Element) | (() => JSX.Element);
type AttachmentPreviewSignature = ((attachment: Attachment) => JSX.Element) | (() => JSX.Element);

const isFilepickerUrl = (url: string): boolean => url.includes('filepicker.io') || url.includes('fp.brandfolder.com') || url.includes('filestackcontent.com') || url.includes('fs.brandfolder.com');

const AssetPreview: FunctionComponent<AssetPreviewProps> = ({
  asset,
  assetBackgroundColor,
  assetType,
  attachmentKeys,
  isDownloadable = true,
  threeSixtyViewer,
}) => {
  const [selectedAttachmentKey, setSelectedAttachmentKey] = useState(attachmentKeys?.[0] || '');
  const [attachments, setAttachments] = useState<{ [attachmentKey: string]: Attachment }>({});
  const [isPreviewRenderError, setIsPreviewRenderError] = useState(false);

  const selectedAttachment = attachments[selectedAttachmentKey];
  const previewType = selectedAttachment?.preview_type;

  const selectedExtension = selectedAttachment?.extension?.toLowerCase();
  const showOpenInNewTabButton = selectedAttachment
    && (WEB_FRIENDLY_ATTACHMENT_TYPES.includes(selectedExtension) || previewType === PreviewType.PDF || previewType === PreviewType.OfficeDoc)
    && !!isDownloadable
    && !isPreviewRenderError
    && !threeSixtyViewer
    && !BFG.brandfolderSettings?.hide_asset_open_in_new_tab
    && !isVideoType(previewType)
    && !selectedAttachment.is_processing
    && (previewType !== PreviewType.Audio);

  const enableOpenInNewTab = showOpenInNewTabButton && selectedAttachment?.preview_type !== PreviewType.PDF;

  const handleUpdateSelectedAttachment = (e: UpdateSelectedAttachmentEvent): void => (
    setSelectedAttachmentKey(e.detail.attachmentKey)
  );

  const openInNewTab = (): void => {
    if (previewType === PreviewType.OfficeDoc || (previewType === PreviewType.Image && isFilepickerUrl(selectedAttachment.url))) {
      window.open(`/viewer/${BFG.brandfolder_slug}/${selectedAttachmentKey}`, '_blank');
    } else if (previewType === PreviewType.Image || previewType === PreviewType.PDF) {
      window.open(selectedAttachment.url, '_blank');
    }
  };

  useEffect(() => {
    window.addEventListener('updateSelectedAttachment', handleUpdateSelectedAttachment);

    return (): void => {
      window.removeEventListener('updateSelectedAttachment', handleUpdateSelectedAttachment);
    };
  }, []);

  useEffect(() => {
    const getAttachmentAsync = async (): Promise<void> => {
      // todo(emmi): include updateFetchControllers for attachment api wrappers
      if (!attachmentKeys?.length || selectedAttachment) return;
      try {
        // just get the additional video fields up front since it's very cheap to grab them
        const additionalAttachmentFields = 'brightcove_account_id,brightcove_video_id,cdn_url,mux_hls_url,preview_type,view_thumbnail_retina,is_processing';
        const attachment = await getAttachment(selectedAttachmentKey, { params: { fields: additionalAttachmentFields } });

        setAttachments({
          ...attachments,
          [selectedAttachmentKey]: attachment.attributes
        });
      } catch (e) {
        throw e;
      }
    };

    getAttachmentAsync();
  }, [attachmentKeys, attachments, selectedAttachment, selectedAttachmentKey]);

  const previewImage = (resource: Attachment | Asset): JSX.Element => (
    <ImagePreview
      assetType={assetType}
      isPreviewRenderError={isPreviewRenderError}
      resource={resource}
      setIsPreviewRenderError={setIsPreviewRenderError}
    />
  );

  const previewColor = (): JSX.Element => (
    <div className="color-preview" data-testid="asset-preview--color" style={assetBackgroundColor} />
  );
  const previewFont = (): JSX.Element => (
    <FontClass asset={asset}>
      Aa Bb Cc Dd Ee Ff Gg Hh Jj Kk Ll Mm Nn Oo Pp Qq Rr Ss Tt Uu Vv Ww Xx Yy Zz 0123456789
    </FontClass>
  );

  const attachmentPreviews: { [key: string]: AttachmentPreviewSignature } = {
    [PreviewType.Audio]: AudioPreview,
    [PreviewType.Image]: previewImage,
    [PreviewType.OfficeDoc]: previewImage,
    [PreviewType.PDF]: BFG.context?.isIE || selectedAttachment?.is_processing ? previewImage : PDFPreview,
    [PreviewType.VideoBrightcove]: BrightcoveVideoPreview,
    [PreviewType.VideoMux]: MuxVideoPreview,
    [PreviewType.VideoOther]: MuxVideoPreview,
    [PreviewType.VideoProcessing]: ProcessingVideoPreview
  };

  const assetPreviews: { [key: string]: AssetPreviewSignature } = {
    // Assets with no attachments (and will never have any attachments)
    [AssetTypes.ExternalMedium]: ExternalMediaPreview,
    [AssetTypes.Text]: TextPreview,
    [AssetTypes.Color]: previewColor,
    [AssetTypes.Font]: previewFont,
    // Assets with no attachments (BUT are capable of having attachments)
    [AssetTypes.GenericFile]: previewImage,
    [AssetTypes.People]: previewImage,
    [AssetTypes.Press]: previewImage,
  };

  const attachmentsPresent = !threeSixtyViewer && !!attachmentKeys?.length && selectedAttachment;
  const noAttachmentsPresent = !threeSixtyViewer && !attachmentKeys?.length;
  const isLoading = !threeSixtyViewer && !!attachmentKeys?.length && !selectedAttachment;

  return (
    <>
      <div
        className={classnames(
          'm-show-full-size-display asset-preview-wrapper',
          {
            'open-in-new-tab': enableOpenInNewTab,
            'responsive-height': assetType === AssetTypes.ExternalMedium,
            video: isVideoType(previewType)
          }
        )}
        data-private
        data-testid="asset-preview--image-container"
        onClick={enableOpenInNewTab ? openInNewTab : undefined}
        onKeyPress={enableOpenInNewTab ? keyPressHelper(openInNewTab) : undefined}
        role={enableOpenInNewTab ? 'button' : undefined}
        style={assetBackgroundColor}
        tabIndex={enableOpenInNewTab ? 0 : undefined}
      >
        {/*
          External Media, Color, Font and Text assets will never have attachments
          Generic File, People and Press assets may have 0 or more attachments
        */}
        {threeSixtyViewer && <div data-testid="asset-preview--three-sixty-viewer" id="three-sixty-viewer" />}
        {attachmentsPresent && attachmentPreviews[selectedAttachment.preview_type](selectedAttachment)}
        {noAttachmentsPresent && assetPreviews[assetType](asset)}
        {isLoading && <BFLoader />}
      </div>
      {!!showOpenInNewTabButton && (
        <TextButton
          className="open-in-new-tab-button"
          data-testid="asset-preview--open-in-new-tab"
          onClick={openInNewTab}
        >
          {bfTranslate('Open in new tab')}
        </TextButton>
      )}
    </>
  );
};

export default AssetPreview;
